import { Button, IconButton, Tag } from '@ads/components-react';
import { EyeIcon, PencilSquareIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import * as Dialog from '@radix-ui/react-dialog';
import { useBlocker } from '@tanstack/react-router';
import type { ComponentType } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  BatchPermissionViewModel,
  ControllerViewModel,
  RoleControllerPermissionSummaryViewModel,
} from '@/shared/api/generated/types';
import { Skeleton } from '@/shared/ui/atoms';
import { EmptyState } from '@/shared/ui/molecules/EmptyState';

// ─── Tipos ────────────────────────────────────────────────────────────────────

export interface PermissionsMatrixProps {
  readonly roleId: number;
  /** Lista completa de controllers — fonte de todas as linhas da matriz. */
  readonly controllers: ControllerViewModel[];
  /** Resumo de permissões já atribuídas ao role — mesclado por `controllerId`. */
  readonly summary: RoleControllerPermissionSummaryViewModel[];
  readonly isLoading: boolean;
  readonly isSaving: boolean;
  readonly onSave: (permissions: BatchPermissionViewModel[]) => void;
  readonly onBack: () => void;
}

type PermKey = 'canRead' | 'canCreate' | 'canUpdate' | 'canDelete';

interface RowState {
  controllerId: number;
  controllerName: string;
  canRead: boolean;
  canCreate: boolean;
  canUpdate: boolean;
  canDelete: boolean;
}

type RowStatus = 'full' | 'partial' | 'none';

type PermLabelKey =
  | 'permissions.matrix.columns.read'
  | 'permissions.matrix.columns.create'
  | 'permissions.matrix.columns.update'
  | 'permissions.matrix.columns.delete';

type StatusLabelKey =
  | 'permissions.matrix.status.full'
  | 'permissions.matrix.status.partial'
  | 'permissions.matrix.status.none';

interface PermColumnDef {
  key: PermKey;
  labelKey: PermLabelKey;
  icon: ComponentType<React.SVGProps<SVGSVGElement>>;
}

const PERM_COLUMNS: PermColumnDef[] = [
  {
    key: 'canRead',
    labelKey: 'permissions.matrix.columns.read',
    icon: EyeIcon,
  },
  {
    key: 'canCreate',
    labelKey: 'permissions.matrix.columns.create',
    icon: PlusIcon,
  },
  {
    key: 'canUpdate',
    labelKey: 'permissions.matrix.columns.update',
    icon: PencilSquareIcon,
  },
  {
    key: 'canDelete',
    labelKey: 'permissions.matrix.columns.delete',
    icon: TrashIcon,
  },
];

const SKELETON_ROW_COUNT = 8;

// ─── Utilitários ──────────────────────────────────────────────────────────────

function getRowStatus(row: RowState): RowStatus {
  const count = [row.canRead, row.canCreate, row.canUpdate, row.canDelete].filter(Boolean).length;
  if (count === 4) return 'full';
  if (count > 0) return 'partial';
  return 'none';
}

const STATUS_TAG: Record<
  RowStatus,
  { status: 'positive' | 'highlight' | 'default'; labelKey: StatusLabelKey }
> = {
  full: { status: 'positive', labelKey: 'permissions.matrix.status.full' },
  partial: {
    status: 'highlight',
    labelKey: 'permissions.matrix.status.partial',
  },
  none: { status: 'default', labelKey: 'permissions.matrix.status.none' },
};

// ─── Componente ───────────────────────────────────────────────────────────────

/**
 * Organism PermissionsMatrix — matriz de permissões CRUD por controller.
 *
 * Exibe botões de toggle com ícone para cada combinação de controller × permissão.
 * O botão Salvar fica habilitado apenas quando há alterações não salvas.
 *
 * @example
 * ```tsx
 * <PermissionsMatrix
 *   roleId={roleId}
 *   summary={summary}
 *   isLoading={isLoading}
 *   isSaving={isSaving}
 *   onSave={handleSave}
 *   onBack={() => navigate({ to: '/access-management' })}
 * />
 * ```
 */
export function PermissionsMatrix({
  roleId,
  controllers,
  summary,
  isLoading,
  isSaving,
  onSave,
  onBack,
}: PermissionsMatrixProps) {
  const { t } = useTranslation('accessControl');

  const [rows, setRows] = useState<RowState[]>([]);
  const [isDirty, setIsDirty] = useState(false);

  const blocker = useBlocker({
    shouldBlockFn: () => isDirty,
    withResolver: true,
  });

  useEffect(() => {
    if (controllers.length === 0) return;

    // A lista de controllers é a fonte de todas as linhas; o summary só traz os
    // controllers que já possuem alguma permissão. Mesclamos por `controllerId`
    // para que controllers sem permissão apareçam (com todos os flags em false)
    // e possam receber novas permissões.
    const permById = new Map(summary.map(item => [item.controllerId, item]));

    const built: RowState[] = controllers.map(controller => {
      const perm = permById.get(controller.id);
      return {
        controllerId: controller.id ?? 0,
        controllerName: controller.name ?? `Controller ${controller.id}`,
        canRead: perm?.canRead ?? false,
        canCreate: perm?.canCreate ?? false,
        canUpdate: perm?.canUpdate ?? false,
        canDelete: perm?.canDelete ?? false,
      };
    });

    setRows(built);
    setIsDirty(false);
  }, [controllers, summary]);

  function togglePerm(controllerId: number, key: PermKey) {
    setRows(prev =>
      prev.map(r => (r.controllerId === controllerId ? { ...r, [key]: !r[key] } : r)),
    );
    setIsDirty(true);
  }

  function handleSave() {
    const batch: BatchPermissionViewModel[] = rows.map(r => ({
      roleId,
      controllerId: r.controllerId,
      canRead: r.canRead,
      canCreate: r.canCreate,
      canUpdate: r.canUpdate,
      canDelete: r.canDelete,
    }));
    onSave(batch);
    // `isDirty` NÃO é zerado aqui: em caso de sucesso, a invalidação do summary
    // dispara um refetch → o efeito reconstrói as linhas e reseta `isDirty`. Em
    // caso de erro, `isDirty` permanece true para que o usuário possa tentar salvar
    // novamente (o botão continua habilitado) e a guarda de navegação siga ativa.
  }

  return (
    <div className="flex flex-col gap-md">
      {/* Tabela */}
      <div className="overflow-hidden rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface p-xxxs">
        <div className="overflow-x-auto">
          <table className="w-full table-fixed">
            <thead>
              <tr className="h-[4.5rem] border-b-hairline border-ctx-layout-border">
                <th
                  scope="col"
                  className="px-xxs py-micro align-middle text-left text-ctx-content-title ts-subtitle-xxs"
                >
                  {isLoading ? (
                    <Skeleton className="h-xxs w-[5rem]" />
                  ) : (
                    t('permissions.matrix.columns.controller')
                  )}
                </th>
                {PERM_COLUMNS.map(({ key, labelKey }) => (
                  <th
                    key={key}
                    scope="col"
                    className="px-xxs py-micro align-middle text-center text-ctx-content-title ts-subtitle-xxs"
                  >
                    {isLoading ? <Skeleton className="mx-auto h-xxs w-sm" /> : t(labelKey)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-ctx-layout-border bg-ctx-layout-surface">
              {isLoading ? (
                Array.from({ length: SKELETON_ROW_COUNT }).map((_, i) => (
                  <tr key={i}>
                    <td className="px-md py-mini">
                      <div className="flex items-center gap-xs">
                        <Skeleton className="size-mini flex-shrink-0" shape="circle" />
                        <Skeleton className="h-mini w-[8rem]" />
                        <Skeleton className="h-mini w-[5rem] rounded-pill" />
                      </div>
                    </td>
                    {PERM_COLUMNS.map((_, j) => (
                      <td key={j} className="px-md py-mini text-center">
                        <Skeleton shape="rect" className="mx-auto h-sm w-sm rounded-md" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={PERM_COLUMNS.length + 1}>
                    <EmptyState title={t('permissions.matrix.empty')} />
                  </td>
                </tr>
              ) : (
                rows.map(row => {
                  const status = getRowStatus(row);
                  const tagDef = STATUS_TAG[status];

                  return (
                    <tr
                      key={row.controllerId}
                      className="h-[4.5rem] transition-colors hover:bg-ctx-interactive-secondaryHover"
                    >
                      <td className="px-xxs py-mini align-middle">
                        <span className="ts-paragraph-xxs text-ctx-content-base">
                          {row.controllerName}
                        </span>
                      </td>
                      <td className="px-xxs py-mini align-middle">
                        <div className="flex justify-center">
                          <Tag
                            label={t(tagDef.labelKey)}
                            status={tagDef.status}
                            size="sm"
                            custom={{ text: 'text-nowrap' }}
                          />
                        </div>
                      </td>
                      {PERM_COLUMNS.map(({ key, icon: Icon }) => (
                        <td key={key} className="px-xxs py-mini align-middle text-center">
                          <IconButton
                            type="button"
                            onClick={() => togglePerm(row.controllerId, key)}
                            ariaLabel={key}
                            icon={Icon}
                            hierarchy={row[key] ? 'primary' : 'secondary'}
                          />
                        </td>
                      ))}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Ações */}
      <div className="flex items-center justify-between">
        <Button size="lg" hierarchy="secondary" onClick={onBack} disabled={isSaving}>
          {t('permissions.actions.back')}
        </Button>
        <Button
          size="lg"
          hierarchy="primary"
          onClick={handleSave}
          disabled={!isDirty}
          isLoading={isSaving}
        >
          {isSaving ? t('permissions.actions.saving') : t('permissions.actions.save')}
        </Button>
      </div>

      {/* Diálogo de aviso de alterações não salvas */}
      <Dialog.Root
        open={blocker.status === 'blocked'}
        onOpenChange={open => {
          if (!open) blocker.reset?.();
        }}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-none z-40 bg-base-neutral-black/intense" />
          <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-md bg-ctx-layout-surface p-xxs shadow-level3">
            <Dialog.Title className="text-ctx-content-title ts-heading-md">
              {t('permissions.unsavedDialog.title')}
            </Dialog.Title>
            <Dialog.Description className="mt-mini text-ctx-content-base ts-paragraph-xs">
              {t('permissions.unsavedDialog.message')}
            </Dialog.Description>
            <div className="mt-md flex w-full items-center gap-micro">
              <Button size="md" hierarchy="secondary" onClick={() => blocker.reset?.()}>
                {t('permissions.unsavedDialog.cancel')}
              </Button>
              <Button size="md" hierarchy="danger" onClick={() => blocker.proceed?.()}>
                {t('permissions.unsavedDialog.confirm')}
              </Button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}
